import * as React from 'react'

import { ChevronDownIcon } from 'lucide-react'

import { UploadingType } from '~/client/graph'
import {
  IExecuteResourceImportMutation,
  IParseResourceImportFileQuery,
  useExecuteResourceImportMutation,
  useParseResourceImportFileLazyQuery,
} from '~/client/graph/operations/generated/Resources.generated'
import { PropertyType } from '~/client/graph/types.generated'
import DndArea from '~/client/src/desktop/components/DndArea/DndArea'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import StruxhubSearchableSelect from '~/client/src/shared/components/StruxhubInputs/StruxhubSearchableSelect/StruxhubSearchableSelect'
import FileType from '~/client/src/shared/enums/FileType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'

interface IProps {
  isOpen: boolean
  onClose: () => void
  projectId?: string
  resourceTypeId?: string
  fileUploadingStore?: FileUploadingStore
  resourceSchema: ResourceSchema
}

export const ResourcesImportDialog: React.FC<IProps> = ({
  isOpen,
  onClose,
  projectId,
  resourceTypeId,
  fileUploadingStore,
  resourceSchema,
}) => {
  const [storagePath, setStoragePath] = React.useState<string | null>(null)
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null)
  const [uploading, setUploading] = React.useState(false)
  const [uploadComplete, setUploadComplete] = React.useState(false)
  const [parseResult, setParseResult] = React.useState<
    IParseResourceImportFileQuery['parseResourceImportFile'] | null
  >(null)
  const [mappings, setMappings] = React.useState<Record<string, string | null>>(
    {},
  )
  const [nameColumnIndex, setNameColumnIndex] = React.useState<number | null>(
    null,
  )
  const [importResult, setImportResult] = React.useState<
    IExecuteResourceImportMutation['executeResourceImport'] | null
  >(null)
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<
    string | null
  >(null)

  const [parseImportFile] = useParseResourceImportFileLazyQuery()

  const [executeImport, { loading: importLoading }] =
    useExecuteResourceImportMutation()

  const { properties, templates } = resourceSchema

  React.useEffect(() => {
    if (templates.length > 0 && !selectedTemplateId) {
      setSelectedTemplateId(templates[0].id)
    }
  }, [templates, selectedTemplateId])

  const handleClose = () => {
    setStoragePath(null)
    setSelectedFile(null)
    setUploading(false)
    setUploadComplete(false)
    setParseResult(null)
    setMappings({})
    setNameColumnIndex(null)
    setImportResult(null)
    onClose()
  }

  const handleFileDrop = async (file: File | Buffer | Blob) => {
    if (file instanceof File) {
      setSelectedFile(file)
      await handleUpload(file)
    }
  }

  const handleImportExecute = async () => {
    if (
      !storagePath ||
      !projectId ||
      !resourceTypeId ||
      !selectedTemplateId ||
      nameColumnIndex === null
    )
      return

    const allMappings: Array<{
      columnIndex: number
      field?: string
      propertyId?: string
    }> = []

    if (nameColumnIndex !== -1) {
      allMappings.push({
        columnIndex: nameColumnIndex,
        field: 'name',
      })
    }

    const propertyMappings = Object.entries(mappings)
      .filter(([, columnHeader]) => columnHeader !== null)
      .map(([propertyName, columnHeader]) => {
        const property = properties.find(p => p.name === propertyName)
        const columnIndex = parseResult?.headers?.indexOf(
          columnHeader as string,
        )

        if (!property?.id || columnIndex === undefined || columnIndex === -1) {
          return null
        }

        return {
          columnIndex,
          propertyId: property.id,
        }
      })
      .filter(
        (mapping): mapping is { columnIndex: number; propertyId: string } =>
          mapping !== null,
      )

    allMappings.push(...propertyMappings)

    try {
      const result = await executeImport({
        variables: {
          projectId,
          storagePath,
          resourceTypeId,
          templateId: selectedTemplateId,
          mappings: allMappings,
        },
      })

      if (result.data?.executeResourceImport) {
        setImportResult(result.data.executeResourceImport)
      }
    } catch (error) {
      console.error('Import failed:', error)
    }
  }

  const handleUpload = async (file: File) => {
    if (!file || !fileUploadingStore || !projectId || !resourceTypeId) return

    setUploading(true)

    try {
      const [result] = await fileUploadingStore.uploadFile(
        file,
        UploadingType.MsExcel,
        projectId,
        file.name,
      )

      if (result?.filePath) {
        setStoragePath(result.filePath)
        setUploadComplete(true)

        const parseQueryResult = await parseImportFile({
          variables: {
            projectId,
            storagePath: result.filePath,
            resourceTypeId,
          },
        })

        const parseData = parseQueryResult.data?.parseResourceImportFile
        setParseResult(parseData)

        if (parseData?.suggestedMappings) {
          const initialMappings: Record<string, string | null> = {}
          parseData.suggestedMappings.forEach(mapping => {
            if (mapping.field === 'name') {
              setNameColumnIndex(mapping.columnIndex)
            } else if (mapping.propertyName && mapping.columnHeader) {
              initialMappings[mapping.propertyName] = mapping.columnHeader
            }
          })
          setMappings(initialMappings)
        }
      }
    } catch (error) {
      console.error('Upload error:', error)
    } finally {
      setUploading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="tw:w-[500px] tw:sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{Localization.translator.import}</DialogTitle>
        </DialogHeader>
        <div className="tw:h-[70vh] tw:overflow-auto">
          {!uploadComplete && !uploading && (
            <DndArea
              accept=".csv,.xlsx,.xlsm"
              fileType={FileType.Excel}
              onDrop={handleFileDrop}
              title={
                <>
                  <div className="text large center">
                    {Localization.translator.uploadEquipmentFile}
                  </div>
                  <div className="text large center">
                    {Localization.translator.orDragYourFileHere}
                  </div>
                </>
              }
              dropzoneClassName="full-width"
            />
          )}
          {uploading && selectedFile && (
            <div>Uploading {selectedFile.name}...</div>
          )}
          {importResult ? (
            <ImportResultView importResult={importResult} />
          ) : (
            uploadComplete &&
            parseResult && (
              <div className="col">
                <div className="text large bold mb10">
                  {Localization.translator.mapColumnsToProperties}
                </div>
                {templates.length > 1 && (
                  <div className="mb10">
                    <StruxhubSearchableSelect
                      label={Localization.translator.template}
                      isRequired
                      options={templates.map(t => ({
                        value: t.id,
                        label: t.name,
                      }))}
                      value={selectedTemplateId}
                      onChange={value =>
                        setSelectedTemplateId(value as string | null)
                      }
                      placeholder={Localization.translator.selectTemplate}
                      allowUserCreation={false}
                    />
                  </div>
                )}
                <div className="mb20">
                  <StruxhubSearchableSelect
                    label={Localization.translator.name}
                    isRequired
                    options={[
                      {
                        value: '-1',
                        label: Localization.translator.notProvided,
                      },
                      ...(parseResult.headers?.map(
                        (header: string, index: number) => ({
                          value: index.toString(),
                          label: header,
                        }),
                      ) || []),
                    ]}
                    value={
                      nameColumnIndex !== null
                        ? nameColumnIndex.toString()
                        : null
                    }
                    onChange={value =>
                      setNameColumnIndex(
                        value !== null ? parseInt(value as string, 10) : null,
                      )
                    }
                    placeholder={Localization.translator.selectTitleColumn}
                    allowUserCreation={false}
                  />
                </div>
                {properties
                  .filter(
                    property =>
                      property.propertyType !== PropertyType.ComputedField &&
                      property.propertyType !== PropertyType.Rollup,
                  )
                  .map(property => {
                    const headerOptions =
                      parseResult.headers?.map((header: string) => ({
                        value: header,
                        label: header,
                      })) || []

                    return (
                      <div key={property.id} className="mb10">
                        <StruxhubSearchableSelect
                          label={property.displayName || property.name}
                          options={headerOptions}
                          value={mappings[property.name] || null}
                          onChange={value =>
                            setMappings(prev => ({
                              ...prev,
                              [property.name]: value as string | null,
                            }))
                          }
                          placeholder={Localization.translator.selectColumn}
                          allowUserCreation={false}
                        />
                      </div>
                    )
                  })}
              </div>
            )
          )}
        </div>
        {importResult ? (
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              {Localization.translator.close}
            </Button>
          </DialogFooter>
        ) : (
          uploadComplete &&
          parseResult && (
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                {Localization.translator.cancel}
              </Button>
              <Button
                variant="default"
                onClick={handleImportExecute}
                disabled={
                  !(
                    !importLoading &&
                    nameColumnIndex !== null &&
                    !!selectedTemplateId
                  ) || importLoading
                }
              >
                {importLoading ? (
                  <Spinner size="xs" />
                ) : (
                  <>{Localization.translator.import}</>
                )}
              </Button>
            </DialogFooter>
          )
        )}
      </DialogContent>
    </Dialog>
  )
}

type ImportResult = NonNullable<
  IExecuteResourceImportMutation['executeResourceImport']
>
type ImportError = ImportResult['errors'][number]

interface IRowGroup {
  row: number
  errors: ImportError[]
}

function groupErrorsByRow(errors: ImportError[]): IRowGroup[] {
  const groups = new Map<number, ImportError[]>()

  for (const error of errors) {
    if (!groups.has(error.row)) groups.set(error.row, [])
    groups.get(error.row)?.push(error)
  }

  return Array.from(groups.entries()).map(([row, rowErrors]) => ({
    row,
    errors: rowErrors,
  }))
}

interface IImportResultViewProps {
  importResult: ImportResult
}

const ImportResultView: React.FC<IImportResultViewProps> = ({
  importResult,
}) => {
  const errorGroups = React.useMemo(
    () => groupErrorsByRow(importResult.errors),
    [importResult.errors],
  )
  const totalErrors = importResult.errors.length
  const [isExpanded, setIsExpanded] = React.useState(totalErrors <= 5)

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <div className="text large bold">
        {Localization.translator.importComplete}
      </div>
      <div className="tw:flex tw:gap-6">
        <div className="tw:flex tw:gap-2">
          <span className="text bold">{Localization.translator.created}</span>
          <span>{importResult.created}</span>
        </div>
        <div className="tw:flex tw:gap-2">
          <span className="text bold">{Localization.translator.failed}</span>
          <span>{importResult.failed}</span>
        </div>
      </div>
      {totalErrors > 0 && (
        <div className="tw:border tw:border-solid tw:border-md3-outline-variant tw:rounded-lg tw:bg-md3-surface-container-low tw:overflow-hidden">
          <button
            type="button"
            className="tw:flex tw:items-start tw:gap-2 tw:w-full tw:py-3 tw:bg-transparent tw:border-0 tw:cursor-pointer tw:outline-none tw:text-left"
            onClick={() => setIsExpanded(prev => !prev)}
          >
            <ChevronDownIcon
              size={20}
              className={`tw:text-md3-on-surface-variant tw:transition-transform tw:duration-200 tw:mt-0.5 tw:shrink-0 ${
                isExpanded ? '' : 'tw:-rotate-90'
              }`}
            />
            <div>
              <div className="tw:text-label-lg tw:text-md3-on-surface">
                {Localization.translator.skippedValues}
              </div>
              <div className="tw:text-body-sm tw:text-md3-on-surface-variant tw:mt-0.5">
                {Localization.translator.skippedValuesDescription}
              </div>
            </div>
          </button>
          {isExpanded && (
            <div className="tw:flex tw:flex-col">
              {errorGroups.map(group => (
                <ErrorRowCard
                  key={group.row}
                  row={group.row}
                  errors={group.errors}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface IErrorRowCardProps {
  row: number
  errors: ImportError[]
}

const ErrorRowCard: React.FC<IErrorRowCardProps> = ({ row, errors }) => {
  const propertiesSkipped = Localization.translator.xPropertiesSkipped(
    errors.length,
  )

  return (
    <div className="tw:border-l-2 tw:border-l-md3-outline-variant tw:border-t tw:border-t-md3-outline-variant tw:border-solid tw:border-r-0 tw:border-b-0 tw:ml-4 tw:pl-3 tw:pr-4 tw:py-3">
      <div className="tw:flex tw:items-center tw:gap-2 tw:mb-2">
        {row > 0 && (
          <span className="tw:text-label-sm tw:bg-md3-surface-container-highest tw:text-md3-on-surface tw:rounded tw:px-2 tw:py-0.5">
            Row {row}
          </span>
        )}
        <span className="tw:text-body-sm tw:text-md3-on-surface-variant">
          {propertiesSkipped}
        </span>
      </div>
      <div className="tw:flex tw:flex-col tw:gap-3">
        {errors.map((error, index) => (
          <div key={index}>
            <div className="tw:flex tw:items-center tw:gap-2 tw:flex-wrap">
              {error.propertyName && (
                <span className="tw:text-label-lg tw:text-md3-on-surface">
                  {error.propertyName}
                </span>
              )}
              {error.value && (
                <span className="tw:text-body-sm tw:bg-md3-surface-container tw:text-md3-on-surface-variant tw:rounded tw:px-1.5 tw:py-0.5 tw:font-mono">
                  {error.value}
                </span>
              )}
            </div>
            <div className="tw:text-body-sm tw:text-md3-on-surface-variant tw:mt-0.5">
              {error.message}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
